import type { ModalSwipeDirection } from './types';

type ModalSwipeListenerSet = {
  touchStart: (touch_event: TouchEvent) => void;
  touchMove: (touch_event: TouchEvent) => void;
  touchEnd: (touch_event: TouchEvent) => void;
  touchCancel: () => void;
};

/**
 * // Class Obj: モーダル内のスワイプ操作を判定する
 * // Note:
 * // - touchstart / touchend を監視し、一定距離以上のスワイプのみ反応します。
 * // - 横スワイプ / 縦スワイプを判定し、方向だけを ModalDisplay 側へ返します。
 * // - 実際のモーダル移動処理は担当しません。
 */
export class ModalSwipeHandler
{
  private static readonly MIN_DISTANCE = 50;
  private static readonly DIRECTION_RATIO = 1.25;

  private readonly mListenerMap: Map<HTMLElement, ModalSwipeListenerSet> = new Map();

  private mStartX: number | null = null;
  private mStartY: number | null = null;
  private mShouldIgnoreCurrentTouch: boolean = false;

  public bind(
    target_element: HTMLElement,
    callback: (swipe_direction: ModalSwipeDirection) => void,
    should_prevent_default: () => boolean = () => true,
    should_ignore_event: (touch_event: TouchEvent) => boolean = () => false,
  ): void
  {
    this.destroyByElement(target_element);

    const listener_set: ModalSwipeListenerSet = {
      touchStart: (touch_event: TouchEvent): void =>
      {
        this.handleTouchStart(touch_event, should_ignore_event);
      },
      touchMove: (touch_event: TouchEvent): void =>
      {
        this.handleTouchMove(touch_event, should_prevent_default);
      },
      touchEnd: (touch_event: TouchEvent): void =>
      {
        this.handleTouchEnd(touch_event, callback);
      },
      touchCancel: (): void =>
      {
        this.resetTouchPosition();
      },
    };

    target_element.addEventListener('touchstart', listener_set.touchStart, { passive: true });
    target_element.addEventListener('touchmove', listener_set.touchMove, { passive: false });
    target_element.addEventListener('touchend', listener_set.touchEnd, { passive: true });
    target_element.addEventListener('touchcancel', listener_set.touchCancel, { passive: true });

    this.mListenerMap.set(target_element, listener_set);
  }

  /**
   * // Method: 登録済み touch event をすべて解除する
   */
  public destroy(): void
  {
    Array.from(this.mListenerMap.keys()).forEach((target_element: HTMLElement) =>
    {
      this.destroyByElement(target_element);
    });

    this.resetTouchPosition();
  }

  /**
   * // Method: 指定要素に登録した touch event を解除する
   */
  private destroyByElement(target_element: HTMLElement): void
  {
    const listener_set: ModalSwipeListenerSet | undefined = this.mListenerMap.get(target_element);

    if (!listener_set)
    {
      return;
    }

    target_element.removeEventListener('touchstart', listener_set.touchStart);
    target_element.removeEventListener('touchmove', listener_set.touchMove);
    target_element.removeEventListener('touchend', listener_set.touchEnd);
    target_element.removeEventListener('touchcancel', listener_set.touchCancel);

    this.mListenerMap.delete(target_element);
  }

  private handleTouchStart(
    touch_event: TouchEvent,
    should_ignore_event: (touch_event: TouchEvent) => boolean,
  ): void
  {
    this.mShouldIgnoreCurrentTouch = should_ignore_event(touch_event);

    if (this.mShouldIgnoreCurrentTouch)
    {
      this.clearTouchPosition();
      return;
    }

    const touch: Touch | undefined = touch_event.touches[0];

    if (!touch)
    {
      return;
    }

    this.mStartX = touch.clientX;
    this.mStartY = touch.clientY;
  }

  private handleTouchMove(
    touch_event: TouchEvent,
    should_prevent_default: () => boolean,
  ): void
  {
    if (this.mShouldIgnoreCurrentTouch)
    {
      return;
    }

    if (!should_prevent_default())
    {
      return;
    }

    if (this.mStartX === null || this.mStartY === null)
    {
      return;
    }

    touch_event.preventDefault();
  }

  private handleTouchEnd(
    touch_event: TouchEvent,
    callback: (swipe_direction: ModalSwipeDirection) => void,
  ): void
  {
    if (this.mShouldIgnoreCurrentTouch)
    {
      this.resetTouchPosition();
      return;
    }

    if (this.mStartX === null || this.mStartY === null)
    {
      return;
    }

    const touch: Touch | undefined = touch_event.changedTouches[0];

    if (!touch)
    {
      this.resetTouchPosition();
      return;
    }

    const diff_x: number = touch.clientX - this.mStartX;
    const diff_y: number = touch.clientY - this.mStartY;

    this.resetTouchPosition();

    const swipe_direction: ModalSwipeDirection | null = this.getSwipeDirection(diff_x, diff_y);

    if (!swipe_direction)
    {
      return;
    }

    callback(swipe_direction);
  }

  private getSwipeDirection(diff_x: number, diff_y: number): ModalSwipeDirection | null
  {
    const abs_x: number = Math.abs(diff_x);
    const abs_y: number = Math.abs(diff_y);

    if (abs_x < ModalSwipeHandler.MIN_DISTANCE && abs_y < ModalSwipeHandler.MIN_DISTANCE)
    {
      return null;
    }

    if (abs_x > abs_y * ModalSwipeHandler.DIRECTION_RATIO)
    {
      return diff_x < 0 ? 'left' : 'right';
    }

    if (abs_y > abs_x * ModalSwipeHandler.DIRECTION_RATIO)
    {
      return diff_y < 0 ? 'up' : 'down';
    }

    return null;
  }

  private clearTouchPosition(): void
  {
    this.mStartX = null;
    this.mStartY = null;
  }

  private resetTouchPosition(): void
  {
    this.clearTouchPosition();
    this.mShouldIgnoreCurrentTouch = false;
  }
}
